// SPDX-License-Identifier: LicenseRef-Proprietary
import React, { useState, useEffect, useRef } from "react";
import { logActivity } from "../lib/storage";
import { useAlerts } from "../../../hooks/useAlerts";

interface NumberFieldProps {
  label: string;
  value: number | string;
  onChange: (n: number) => void;
  suffix?: string;
  min?: number;
  step?: number | string;
  max?: number;
  disabled?: boolean;
  "aria-label"?: string;
  compact?: boolean;
  hidden?: boolean;
  labelHidden?: boolean;
  citation?: React.ReactNode;
  helpText?: string;
  warning?: string;
  error?: string;
  labelSuffix?: React.ReactNode;
}

export const NumberField: React.FC<NumberFieldProps> = ({
  label,
  value,
  onChange,
  suffix,
  min = 0,
  step = "any",
  max,
  disabled = false,
  hidden = false,
  "aria-label": ariaLabel,
  compact = false,
  labelHidden = false,
  citation,
  helpText,
  warning,
  error,
  labelSuffix,
}) => {
  const [localValue, setLocalValue] = useState<string>(String(value));
  const timeoutRef = useRef<number | null>(null);
  const lastExternalValueRef = useRef<number | string | null>(value);
  const { showWarning } = useAlerts();
  const lastClampedValueRef = useRef<number | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const citationWrapperRef = useRef<HTMLDivElement>(null);
  const [citationWidth, setCitationWidth] = useState<number | undefined>(undefined);

  useEffect(() => {
    // External update detected - clear any pending debounce timeout
    // This prevents stale debounced callbacks from overwriting newly loaded values
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Update ref to track the latest external value
    lastExternalValueRef.current = value;
    
    // Reset clamped value tracking when external value changes
    const numValue = Number(value);
    if (!isNaN(numValue) && max !== undefined && numValue < max) {
      lastClampedValueRef.current = null;
    }
    
    // Update localValue to match external prop if they differ
    // Use functional setState to get current localValue without adding it to deps
    setLocalValue((prev) => {
      const numLocal = Number(prev);
      
      if (numValue !== numLocal && !isNaN(numValue)) {
        return String(value);
      } else if (prev === '' && value !== 0 && value !== '') {
        return String(value);
      }
      return prev;
    });
  }, [value, max]);

  useEffect(() => {
    const numLocal = Number(localValue);
    const numProp = Number(value);

    if (localValue === '' || localValue === '-') return;

    if (!isNaN(numLocal) && numLocal !== numProp) {
      // Capture the external value at the time we schedule the debounce
      const externalValueAtScheduleTime = lastExternalValueRef.current;
      
      timeoutRef.current = window.setTimeout(() => {
        // Ignore stale callbacks after external changes
        if (lastExternalValueRef.current !== externalValueAtScheduleTime) {
          return;
        }
        
        // Clamp value to min/max bounds before calling onChange
        let clampedValue = numLocal;
        if (min !== undefined && clampedValue < min) {
          clampedValue = min;
        }
        if (max !== undefined && clampedValue > max) {
          clampedValue = max;
          // Show warning notification if clamped to max and we haven't shown it for this value yet
          if (lastClampedValueRef.current !== clampedValue) {
            showWarning(`Maximum value for ${label} is ${max}. Value has been adjusted.`);
            lastClampedValueRef.current = clampedValue;
          }
        } else {
          lastClampedValueRef.current = null;
        }
        
        onChange(clampedValue);
        logActivity(`Updated number field: ${label}, value: ${clampedValue}`);
        timeoutRef.current = null;
      }, 2000);

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      };
    }
  }, [localValue, value, onChange, label, min, max, showWarning]);

  // Match citation wrapper width to input element width
  useEffect(() => {
    if (!citation) {
      setCitationWidth(undefined);
      return;
    }

    const updateWidth = () => {
      const el = inputRef.current;
      if (!el) return;

      const w = el.getBoundingClientRect().width;
      if (w > 0) {
        setCitationWidth(w);
      }
    };
    
    const el = inputRef.current;
    if (!el) return;

    // Use ResizeObserver for accurate tracking
    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(el);

    // Initial measurement
    updateWidth();
    
    return () => {
      resizeObserver.disconnect();
    };
  }, [citation, suffix, localValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setLocalValue(inputValue);
  };

  const handleBlur = () => {
    // Cancel any pending debounced update
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    const numLocal = Number(localValue);
    
    if (localValue === '' || isNaN(numLocal)) {
      setLocalValue(String(value));
    } else {
      // Clamp value to min/max bounds before calling onChange
      let clampedValue = numLocal;
      if (min !== undefined && clampedValue < min) {
        clampedValue = min;
      }
      if (max !== undefined && clampedValue > max) {
        clampedValue = max;
        // Show warning notification if clamped to max and we haven't shown it for this value yet
        if (lastClampedValueRef.current !== clampedValue) {
          showWarning(`Maximum value for ${label} is ${max}. Value has been adjusted.`);
          lastClampedValueRef.current = clampedValue;
        }
      } else {
        lastClampedValueRef.current = null;
      }
      
      // Flush the update immediately (not just schedule it)
      if (clampedValue !== Number(value)) {
        onChange(clampedValue);
        logActivity(`Updated number field (blur): ${label}, value: ${clampedValue}`);
      }
      setLocalValue(String(clampedValue));
    }
  };

  return (
    <div className={`vaccine-demo__number-field ${compact ? 'vaccine-demo__number-field--compact' : ''}`}  style={{ display: hidden ? 'none' : 'block' }}>
      <div className="vaccine-demo__label-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', minHeight: '22px' }}>
        <label className={`vaccine-demo__label ${labelHidden ? 'vaccine-demo__label--hidden' : ''}`} htmlFor={`input-${label.replace(/\s+/g, '-').toLowerCase()}`} style={{ minWidth: 0, flex: 1, overflowWrap: 'break-word' }}>
          {label}
        </label>
        {labelSuffix && <div style={{ flexShrink: 0 }}>{labelSuffix}</div>}
      </div>
      <div className="vaccine-demo__input-group">
        <input
          ref={inputRef}
          id={`input-${label.replace(/\s+/g, '-').toLowerCase()}`}
          type="number"
          value={localValue}
          min={min}
          step={step}
          max={max}
          disabled={disabled}
          onChange={handleChange}
          onBlur={handleBlur}
          className={`vaccine-demo__input ${error ? 'vaccine-demo__input--error' : ''}`}
          aria-label={ariaLabel || label}
          style={error ? { borderColor: '#ef4444' } : {}}
        />
        {suffix && (
          <span className="vaccine-demo__suffix" aria-hidden="true">
            {suffix}
          </span>
        )}
      </div>
      {error && (
        <div className="vaccine-demo__error-text" style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
          {error}
        </div>
      )}
      {warning && !error && (
        <div className="vaccine-demo__help-text" style={{ fontSize: '12px', color: '#f59e0b', marginTop: '4px' }}>
          {warning}
        </div>
      )}
      {helpText && !error && !warning && (
        <div className="vaccine-demo__help-text" style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
          {helpText}
        </div>
      )}
      {citation && (
        <div 
          ref={citationWrapperRef}
          className="vaccine-demo__citation-wrapper" 
          style={{ 
            marginTop: '2px',
            width: citationWidth ? `${citationWidth}px` : undefined,
            maxWidth: citationWidth ? `${citationWidth}px` : '300px'
          }}
        >
          {citation}
        </div>
      )}
    </div>
  );
};
